<script lang="ts">
  /**
   * Innstillinger for hele appen (tannhjulet øverst). Samler det som sjelden
   * endres, så det ikke ligger fremme der man bruker appen til daglig:
   * lyskalibreringen (PPFD), strømprisen, varsler og utlogging.
   * Innstillinger for én kasse (drift/testmodus, navn, sletting) ligger på
   * tannhjulet inne i kassa (KasseInnstillinger).
   */
  import { supabase } from '../lib/supabase';
  import { user } from '../lib/stores';
  import { strompris, settStrompris, ppfdMaks, settPpfdMaks, LYS } from '../lib/settings';
  import { visFeil, visOk } from '../lib/toast';
  import Varselinnstillinger from './Varselinnstillinger.svelte';

  let { onLukk }: { onLukk: () => void } = $props();

  let ppfdUtkast = $state(String($ppfdMaks));
  let prisUtkast = $state($strompris.toFixed(2).replace('.', ','));
  let bekreftLoggUt = $state(false);
  let loggerUt = $state(false);

  function lagrePpfd() {
    if (settPpfdMaks(ppfdUtkast)) {
      ppfdUtkast = String($ppfdMaks);
      visOk('Referansemålingen er lagret. DLI følger denne og avstanden på kassa.');
    } else {
      visFeil('Skriv et tall mellom 20 og 1500, målt ved 100 % lys.');
    }
  }

  function tilbakeTilStandard() {
    settPpfdMaks(LYS.standardPpfd);
    ppfdUtkast = String(LYS.standardPpfd);
    visOk(`Tilbake til standardanslaget (${LYS.standardPpfd}).`);
  }

  function lagrePris() {
    if (settStrompris(prisUtkast)) {
      prisUtkast = $strompris.toFixed(2).replace('.', ',');
      visOk('Strømprisen er lagret.');
    } else {
      visFeil('Skriv prisen i kroner per kWh, for eksempel 1,50.');
    }
  }

  async function loggUt() {
    if (loggerUt) return;
    loggerUt = true;
    // Bare denne enheten: standarden ('global') logget også ut telefonen når
    // man logget ut på PC-en. Uten nett feiler kallet — si fra i stedet.
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    loggerUt = false;
    if (error) {
      visFeil('Fikk ikke logget ut. Sjekk nettet og prøv igjen.');
      return;
    }
    onLukk();
  }

  // Bekreftelsen står bare åpen en liten stund, som «Slett» ellers i appen.
  $effect(() => {
    if (!bekreftLoggUt) return;
    const t = setTimeout(() => (bekreftLoggUt = false), 6000);
    return () => clearTimeout(t);
  });
</script>

<div class="font-display text-[25px] font-semibold leading-[1.05]">Innstillinger</div>
<div class="font-mono text-[11px] text-text-muted mt-1">{$user?.email ?? ''}</div>

<div class="flex flex-col gap-5 mt-5">
  <section>
    <h3 class="label mb-1.5">Lyset</h3>
    <p class="text-sm">{LYS.navn}</p>
    <p class="text-[12px] text-text-muted">{LYS.watt} W per kasse ved 100 %</p>
  </section>

  <section>
    <h3 class="label mb-1.5">Kalibrering av lyset</h3>
    <p class="text-[12px] text-text-muted leading-snug mb-2">
      Referansemåling ved 100 % lys og 13,5 cm fra LED-underkant, i µmol/m²/s (PPFD).
      Standard er {LYS.standardPpfd}. Avstanden til plantetopp velges inne i vekstlys-arket på hver kasse.
      En endring her skalerer hele avstandskurven. Mål derfor ved 13,5 cm når du kalibrerer denne verdien.
    </p>
    <div class="flex items-center gap-2">
      <input
        bind:value={ppfdUtkast}
        inputmode="numeric"
        class="input !w-28 font-mono"
        aria-label="PPFD ved 100 % lys og 13,5 cm, µmol/m²/s"
        onkeydown={(e) => e.key === 'Enter' && lagrePpfd()}
      />
      <span class="text-[12px] text-text-muted">µmol/m²/s</span>
      <button class="btn-secondary !py-2 ml-auto" onclick={lagrePpfd}>Lagre</button>
    </div>
    {#if $ppfdMaks !== LYS.standardPpfd}
      <button class="text-[12px] text-text-muted hover:text-text underline mt-2" onclick={tilbakeTilStandard}>
        Tilbake til standardanslaget ({LYS.standardPpfd})
      </button>
    {/if}
  </section>

  <section>
    <h3 class="label mb-1.5">Strømpris</h3>
    <p class="text-[12px] text-text-muted leading-snug mb-2">Brukes bare til overslaget over hva lyset koster.</p>
    <div class="flex items-center gap-2">
      <input
        bind:value={prisUtkast}
        inputmode="decimal"
        class="input !w-28 font-mono"
        aria-label="Strømpris i kroner per kWh"
        onkeydown={(e) => e.key === 'Enter' && lagrePris()}
      />
      <span class="text-[12px] text-text-muted">kr/kWh</span>
      <button class="btn-secondary !py-2 ml-auto" onclick={lagrePris}>Lagre</button>
    </div>
  </section>

  <Varselinnstillinger />

  <section class="pt-3 border-t border-border">
    {#if bekreftLoggUt}
      <p class="text-[12px] text-text-muted text-center mb-2">Logge ut på denne enheten?</p>
      <div class="flex gap-2.5">
        <button class="btn-secondary flex-1" onclick={() => (bekreftLoggUt = false)}>Avbryt</button>
        <button class="btn-primary flex-1" onclick={loggUt} disabled={loggerUt}>
          {loggerUt ? 'Logger ut…' : 'Logg ut'}
        </button>
      </div>
    {:else}
      <button class="btn-secondary w-full" onclick={() => (bekreftLoggUt = true)}>Logg ut</button>
    {/if}
  </section>

  <a href="/oversikt.html" class="text-[12px] text-text-muted hover:text-text text-center underline">
    Byggeguiden og tegningene
  </a>
</div>
